import { useState, useEffect, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { Row, Col } from 'react-bootstrap';
import Dropzone from 'components/base/Dropzone';
import Button from 'components/base/Button';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faCloudArrowUp,
  faClipboardList,
  faClock,
  faCheckCircle,
  faExclamationCircle
} from '@fortawesome/free-solid-svg-icons';
import { uploadLogFile, fetchCSAPlusProject, listCSAPlusUploads, CSAPlusUploadItem } from '../data-access/csa-plus.api';
import { setToast } from 'components/Toasts/data-access/toast-reducer';
import { useDispatch } from 'react-redux';

const ACCEPTED_TYPES = {
  'text/csv': ['.csv'],
  'application/csv': ['.csv'],
  'text/plain': ['.csv']
};

function isLogUploadItem(u: CSAPlusUploadItem): boolean {
  const t = (u.doc_type || '').toLowerCase();
  return t === 'log' || t === 'sentinel_log';
}

const formatDate = (dateStr: string) => {
  const d = new Date(dateStr);
  return Number.isNaN(d.getTime()) ? dateStr : d.toLocaleString();
};

const LogsTab = () => {
  const { projectId } = useParams<{ projectId: string }>();
  const dispatch = useDispatch();
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [isUploading, setIsUploading] = useState(false);
  const [uploads, setUploads] = useState<CSAPlusUploadItem[]>([]);
  const [isLoadingUploads, setIsLoadingUploads] = useState(true);
  const [dropzoneKey, setDropzoneKey] = useState(0);
  const [lastUploadResult, setLastUploadResult] = useState<{ total_events: number; upload_id: string } | null>(null);

  const loadUploads = useCallback(async () => {
    if (!projectId) return;
    setIsLoadingUploads(true);
    try {
      const project = await fetchCSAPlusProject(projectId);
      const list = listCSAPlusUploads(project?.uploads).filter(isLogUploadItem);
      setUploads(list);
    } catch (err) {
      console.error('Failed to fetch uploads:', err);
      setUploads([]);
    } finally {
      setIsLoadingUploads(false);
    }
  }, [projectId]);

  useEffect(() => {
    loadUploads();
  }, [loadUploads]);

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const handleDrop = (acceptedFiles: File[], _fileRejections: unknown[], _event: unknown) => {
    setSelectedFile(acceptedFiles[0] ?? null);
  };

  const handleUpload = async () => {
    if (!projectId || !selectedFile) {
      dispatch(
        setToast({
          show: true,
          title: 'No file selected',
          message: 'Please select a CSV log file to upload.'
        })
      );
      return;
    }

    const userId = localStorage.getItem('userId') || undefined;
    setIsUploading(true);
    try {
      const result = await uploadLogFile({ file: selectedFile, projectId, userId });
      setLastUploadResult({ total_events: result.total_events, upload_id: result.upload_id });
      dispatch(
        setToast({
          show: true,
          title: 'Log uploaded',
          message: `Parsed ${result.total_events} events successfully.`
        })
      );
      setSelectedFile(null);
      setDropzoneKey(k => k + 1);
      await loadUploads();
    } catch (err: any) {
      console.error('Log upload failed:', err);
      dispatch(
        setToast({
          show: true,
          title: 'Upload failed',
          message: err?.response?.data?.message || err?.message || 'Failed to upload log file.'
        })
      );
    } finally {
      setIsUploading(false);
    }
  };

  return (
    <div className="p-4 p-lg-5">
      <h5 className="fw-bold mb-1 text-body">Logs</h5>
      <p className="text-body-secondary mb-4 mb-lg-5 small">
        Upload CSV log files (e.g. Sentinel exports) for audit and compliance analysis.
      </p>

      <Row className="g-4 g-lg-5">
        <Col xs={12} lg={6}>
          <div className="rounded-3 shadow-sm border p-4 bg-white h-100">
            <h6 className="fw-semibold mb-4 text-body">Upload log document</h6>
            <div className="mb-4">
              <Dropzone
                key={dropzoneKey}
                accept={ACCEPTED_TYPES}
                onDrop={handleDrop}
                reactDropZoneProps={{ multiple: false, maxFiles: 1 }}
              >
                <div className="text-center py-5">
                  <FontAwesomeIcon
                    icon={faCloudArrowUp}
                    className="text-body-tertiary mb-3"
                    size="2x"
                    style={{ opacity: 0.7 }}
                  />
                  <p className="text-body-tertiary mb-1 fw-semibold">Drag and drop your file here</p>
                  <p className="text-muted small mb-0">or click to browse · Accepts .csv</p>
                </div>
              </Dropzone>
            </div>
            <Button
              variant="primary"
              className="csa-upload-btn d-flex align-items-center justify-content-center gap-2 w-100 py-2"
              style={{ backgroundColor: '#252a36', borderColor: '#252a36' }}
              onClick={handleUpload}
              disabled={!selectedFile || isUploading}
            >
              {isUploading ? (
                <>
                  <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true" />
                  Uploading...
                </>
              ) : (
                <>
                  <FontAwesomeIcon icon={faCloudArrowUp} />
                  Upload
                </>
              )}
            </Button>
            {lastUploadResult && (
              <div className="mt-3 p-3 rounded-2 border bg-light d-flex align-items-center gap-2 small text-body-secondary">
                <FontAwesomeIcon icon={faCheckCircle} className="text-success" />
                <span>
                  Last upload: <strong>{lastUploadResult.total_events}</strong> events parsed &middot; ID:{' '}
                  <code className="text-muted">{lastUploadResult.upload_id}</code>
                </span>
              </div>
            )}
          </div>
        </Col>

        <Col xs={12} lg={6}>
          <div className="rounded-3 shadow-sm border p-4 bg-white h-100">
            <h6 className="fw-semibold mb-4 text-body">Recent uploads</h6>
            {isLoadingUploads ? (
              <div className="text-center py-6">
                <div className="spinner-border text-primary" role="status">
                  <span className="visually-hidden">Loading...</span>
                </div>
                <p className="text-muted small mt-2 mb-0">Loading uploads...</p>
              </div>
            ) : uploads.length === 0 ? (
              <div className="text-center py-6">
                <FontAwesomeIcon
                  icon={faClipboardList}
                  className="text-body-tertiary mb-2"
                  size="2x"
                  style={{ opacity: 0.5 }}
                />
                <p className="text-muted small mb-0">No log uploads yet. Upload a document to get started.</p>
              </div>
            ) : (
              <div className="d-flex flex-column gap-2" style={{ maxHeight: '340px', overflowY: 'auto' }}>
                {uploads.map(upload => (
                  <div key={upload.upload_id} className="rounded-2 p-3 border bg-light d-flex align-items-start gap-3">
                    <div className="flex-shrink-0 mt-1">
                      {upload.status === 'uploaded' ? (
                        <FontAwesomeIcon icon={faCheckCircle} className="text-success" size="sm" />
                      ) : (
                        <FontAwesomeIcon icon={faExclamationCircle} className="text-warning" size="sm" />
                      )}
                    </div>
                    <div className="flex-grow-1 min-w-0">
                      <p className="fw-semibold mb-1 text-truncate small" title={upload.original_filename}>
                        {upload.original_filename}
                      </p>
                      <div className="d-flex align-items-center gap-2 text-muted small">
                        <FontAwesomeIcon icon={faClock} size="xs" />
                        <span>{formatDate(upload.uploaded_at)}</span>
                      </div>
                      <span className="badge bg-light text-dark border mt-1 small">{upload.doc_type}</span>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </Col>
      </Row>
    </div>
  );
};

export default LogsTab;
